import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CalendarClock, CheckCircle2, AlertTriangle, Search } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const _tomorrow9am = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const RECENT_KEY = 'c30_scheduled_call_recent_assignees';

/**
 * Bulk-schedule one Zoho Call per selected branch (variant). Reuses the
 * single-call endpoint `/api/zoho/schedule-call` once per branch — no
 * dedicated bulk backend needed since the tap is small (≤25 calls per
 * customer card in practice). The user picks ONE date+assignee+purpose
 * and we resolve each branch's account by name search → top match.
 */
const BulkScheduleBranchesModal = ({ isOpen, onClose, customerName, variants = [] }) => {
  const [selectedSet, setSelectedSet] = useState(new Set());
  const [scheduleFor, setScheduleFor] = useState(_tomorrow9am());
  const [purpose, setPurpose] = useState('Price Enquiry');
  const [purposes, setPurposes] = useState([]);
  const [description, setDescription] = useState('');
  const [callTag, setCallTag] = useState('');
  const [recentTags, setRecentTags] = useState([]);

  // Assignee
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [assigneeResults, setAssigneeResults] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [recentAssignees, setRecentAssignees] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);  // { done, total, results: [{branch, ok, message}] }

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setSelectedSet(new Set(variants));  // pre-select all branches
    setScheduleFor(_tomorrow9am());
    setPurpose('Price Enquiry');
    setDescription('');
    setCallTag('');
    setAssigneeQuery('');
    setAssigneeResults([]);
    setSelectedAssignee(null);
    setProgress(null);
    setSubmitting(false);
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRecentAssignees(parsed.slice(0, 6));
      }
    } catch (_) { /* ignore */ }
    api.get(`${API}/api/zoho/call-purposes`)
      .then((r) => setPurposes(r.data?.purposes || []))
      .catch(() => {});
    api.get(`${API}/api/zoho/recent-tags`)
      .then((r) => setRecentTags(r.data?.tags || []))
      .catch(() => {});
  }, [isOpen, variants]);

  // Debounced assignee search
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const q = assigneeQuery;
      if (!q || q.trim().length < 2) { setAssigneeResults([]); return; }
      setAssigneeLoading(true);
      api.get(`${API}/api/users/search-light`, { params: { q } })
        .then((r) => setAssigneeResults(r.data?.users || []))
        .catch(() => setAssigneeResults([]))
        .finally(() => setAssigneeLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [assigneeQuery, isOpen]);

  const toggleVariant = (v) => {
    setSelectedSet((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };

  const pickAssignee = (u) => {
    setSelectedAssignee(u);
    setAssigneeQuery(u.display_name || u.email);
    setAssigneeResults([]);
  };

  const saveRecentAssignee = (u) => {
    try {
      const trimmed = [
        { id: u.id, email: u.email, display_name: u.display_name },
        ...recentAssignees.filter((r) => r.email !== u.email),
      ].slice(0, 6);
      setRecentAssignees(trimmed);
      localStorage.setItem(RECENT_KEY, JSON.stringify(trimmed));
    } catch (_) { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!selectedAssignee?.email) return toast.error('Pick an assignee');
    if (!scheduleFor) return toast.error('Pick a date & time');
    const branches = Array.from(selectedSet);
    if (branches.length === 0) return toast.error('Select at least one branch');

    setSubmitting(true);
    setProgress({ done: 0, total: branches.length, results: [] });

    const results = [];
    for (let i = 0; i < branches.length; i += 1) {
      const branch = branches[i];
      try {
        // Resolve Zoho account from branch name
        const sr = await api.get(`${API}/api/zoho/search-accounts`, { params: { q: branch } });
        const top = (sr.data?.accounts || [])[0];
        if (!top?.id) {
          results.push({ branch, ok: false, message: 'No matching Zoho account' });
        } else {
          const schedRes = await api.post(`${API}/api/zoho/schedule-call`, {
            account_id: top.id,
            contact_id: null,
            call_start_time: scheduleFor,
            call_duration_minutes: 5,
            call_purpose: purpose,
            subject: `${purpose} — ${top.name || branch}`,
            description: description || null,
            assignee_email: selectedAssignee.email,
          });
          // Save tag for this call if provided
          const callId = schedRes.data?.call_id;
          if (callId && callTag.trim()) {
            api.post(`${API}/api/zoho/call-tags`, { call_id: callId, tag: callTag.trim() }).catch(() => {});
          }
          results.push({ branch, ok: true, message: top.name || branch });
        }
      } catch (e) {
        results.push({ branch, ok: false, message: e?.response?.data?.detail || 'Schedule failed' });
      }
      setProgress({ done: i + 1, total: branches.length, results: [...results] });
    }

    saveRecentAssignee(selectedAssignee);
    window.dispatchEvent(new Event('c30:calls-changed'));
    const okCount = results.filter((r) => r.ok).length;
    if (okCount === branches.length) {
      toast.success(`Scheduled ${okCount} call${okCount === 1 ? '' : 's'} for ${selectedAssignee.display_name || selectedAssignee.email}`);
    } else {
      toast.error(`Scheduled ${okCount} of ${branches.length}. See details below.`);
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bulk-schedule-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-purple-600" />
            Bulk-schedule calls — {customerName}
          </DialogTitle>
          <DialogDescription>
            Pick one date, one assignee, and the branches you want a call scheduled against. We'll create one Zoho Call per selected branch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Branch checkboxes */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Branches ({selectedSet.size}/{variants.length})</Label>
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-1">
              {variants.length === 0 && (
                <div className="text-xs text-gray-500 italic">No branches grouped under this customer.</div>
              )}
              {variants.map((v) => (
                <label
                  key={v}
                  className="flex items-center gap-2 cursor-pointer hover:bg-white px-2 py-1 rounded transition-colors text-sm"
                  data-testid={`bulk-schedule-branch-${v}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(v)}
                    onChange={() => toggleVariant(v)}
                    disabled={submitting}
                  />
                  <span className="text-gray-800">{v}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-1.5 text-[11px]">
              <button
                type="button"
                className="text-purple-700 hover:underline"
                onClick={() => setSelectedSet(new Set(variants))}
                disabled={submitting}
              >Select all</button>
              <button
                type="button"
                className="text-gray-500 hover:underline"
                onClick={() => setSelectedSet(new Set())}
                disabled={submitting}
              >Clear</button>
            </div>
          </div>

          {/* Date / Purpose */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Scheduled for</Label>
              <Input
                type="datetime-local"
                value={scheduleFor}
                onChange={(e) => setScheduleFor(e.target.value)}
                disabled={submitting}
                className="mt-1 rounded-lg border-2 border-gray-300 h-10"
                data-testid="bulk-schedule-datetime"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Purpose</Label>
              <select
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                disabled={submitting}
                className="mt-1 w-full rounded-lg border-2 border-gray-300 h-10 px-3 text-sm bg-white"
                data-testid="bulk-schedule-purpose"
              >
                {(purposes.length ? purposes : ['Price Enquiry', 'Follow-up', 'Project']).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Assignee */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Assign to</Label>
            <div className="mt-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input
                value={assigneeQuery}
                onChange={(e) => { setAssigneeQuery(e.target.value); setSelectedAssignee(null); }}
                placeholder="Search teammate by name or email"
                disabled={submitting}
                className="pl-9 rounded-lg border-2 border-gray-300 h-10"
                data-testid="bulk-schedule-assignee-search"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="bulk-sched-assignee-q"
                role="presentation"
                aria-autocomplete="list"
              />
              {!selectedAssignee && (assigneeLoading || assigneeResults.length > 0) && (
                <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-lg border border-gray-200 shadow-md max-h-44 overflow-y-auto">
                  {assigneeLoading && <div className="p-3 text-center text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Searching…</div>}
                  {!assigneeLoading && assigneeResults.map((u) => (
                    <button
                      type="button"
                      key={u.id || u.email}
                      onClick={() => pickAssignee(u)}
                      className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b last:border-0"
                      data-testid={`bulk-schedule-assignee-${u.email}`}
                    >
                      <div className="font-semibold text-gray-900">{u.display_name || u.email}</div>
                      <div className="text-[11px] text-gray-500">{u.email}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {recentAssignees.length > 0 && !selectedAssignee && (
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold mr-1 self-center">Recent</span>
                {recentAssignees.map((r) => (
                  <button
                    key={r.email}
                    type="button"
                    onClick={() => pickAssignee(r)}
                    disabled={submitting}
                    className="text-[11px] bg-purple-50 hover:bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full"
                  >
                    {r.display_name || r.email}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Note (optional)</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              maxLength={500}
              rows={2}
              placeholder="Same note attached to every scheduled call"
              className="mt-1 w-full rounded-lg border-2 border-gray-300 px-3 py-2 text-sm"
              data-testid="bulk-schedule-note"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Call Tag (optional)</Label>
            <p className="text-[10px] text-gray-400 mb-1">Short label shown as a pill on each call card — hover the pill to see the note.</p>
            <input
              type="text"
              value={callTag}
              onChange={(e) => setCallTag(e.target.value)}
              disabled={submitting}
              maxLength={80}
              placeholder="e.g. Q3 Range, Price Review, New Account"
              className="w-full rounded-lg border-2 border-gray-300 h-9 px-3 text-sm bg-white disabled:opacity-50"
              data-testid="bulk-schedule-tag-input"
            />
            {recentTags.length > 0 && !submitting && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold self-center mr-1">Recent</span>
                {recentTags.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setCallTag(callTag === t ? '' : t)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                      callTag === t
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                    }`}
                    data-testid={`bulk-schedule-recent-tag-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          {progress && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 max-h-48 overflow-y-auto" data-testid="bulk-schedule-progress">
              <div className="text-xs font-bold text-gray-700 mb-1.5">Progress: {progress.done} / {progress.total}</div>
              {progress.results.map((r, idx) => (
                <div key={`${r.branch}-${idx}`} className="flex items-start gap-2 text-xs py-0.5">
                  {r.ok
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                    : <AlertTriangle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />}
                  <span className="font-semibold text-gray-800">{r.branch}</span>
                  <span className="text-gray-500 truncate">— {r.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="bulk-schedule-cancel">
            {progress?.done === progress?.total && progress?.total > 0 ? 'Done' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selectedSet.size === 0 || !selectedAssignee}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="bulk-schedule-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Scheduling…</> : <><CalendarClock className="w-4 h-4 mr-1" />Schedule {selectedSet.size} call{selectedSet.size === 1 ? '' : 's'}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkScheduleBranchesModal;
